import { Col, Form, Input, Row, Select } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, MaxLengthInput } from 'ui';
import InfoTooltip from '../../../ui/info-tooltip';
import { allCreateOrderFields } from '../../../utils/app-configs';

const { useForm } = Form;

const StyledSelect = styled(Select)`
  font-size: 16px;
  font-weight: 400;
`;

const Buttons = styled.div`
  display: flex;
  flex-flow: row nowrap;
  > :first-child {
    margin-right: 20px;
  }
`;

const SimplifiedCreateOrderForm = ({
  isLoading,
  isSubmitting,
  intl,
  cancel,
  handleSubmit,
}) => {
  const [form] = useForm();
  const { serviceTypes, businessSubTypes, isFiscalCodeServiceCenter } =
    useSelector(state => state.order.create);
  const orderDescription = useSelector(state =>
    state.smartAction.actionForCreatingOrder.description,
  );
  const [visitTypes, setVisitTypes] = useState([]);
  const [paidBys, setPaidBys] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [fieldsToRender, setFieldsToRender] = useState(['serviceType']);
  const [additionalFields, setAdditionalFields] = useState([]);

  useEffect(() => {
    if (serviceTypes.length === 1) {
      form.setFieldsValue({
        visitType: serviceTypes[0].id,
      });
    }
  }, [serviceTypes]);

  const conditionsAreValid = (conditions) => {
    if (conditions?.length) {
      const allConditions = conditions?.map((c) => {
        if (c.split('_')[0] && c.split('_')[0] === 'visit') {
          return c.split('_')[1] && c.split('_')[1] === form.getFieldValue('visitType');
        }
        return true;
      });
      return allConditions.every(c => c);
    }
    return true;
  };

  const handleAdditionalFieldSelect = (cb) => {
    setFieldsToRender(s => s.find(i => i === 'description') ? s : [...s, 'description']);
    if (cb) {
      cb();
    }
  };

  const checkBusinessSubTypes = (value) => {
    if (businessSubTypes[value]?.length) {
      setFieldsToRender(s => [...s, 'businessSubType']);
      if (
        businessSubTypes[value].length === 1 &&
        businessSubTypes[value][0]?.isDefault
      ) {
        setAdditionalFields(s => [
          ...s,
          {
            id: 'businessSubType',
            dropdownValues: [],
            disabled: !isFiscalCodeServiceCenter,
            onSelect: () => handleAdditionalFieldSelect(),
          },
        ]);
        form.setFieldsValue({
          businessSubType: businessSubTypes[value][0].additionalParameter,
        });
      } else if (businessSubTypes[value].length >= 1) {
        setAdditionalFields(s => [
          ...s,
          {
            id: 'businessSubType',
            dropdownValues: businessSubTypes[value].map(m => ({
              id: m.additionalParameter,
              description: m.description,
            })),
            disabled: !isFiscalCodeServiceCenter,
            onSelect: () => handleAdditionalFieldSelect(),
          },
        ]);
        const defaultValue = businessSubTypes[value].find(v => v.isDefault);
        if (defaultValue) {
          form.setFieldsValue({
            businessSubType: defaultValue.additionalParameter,
          });
        }
      }
    }
  };

  const renderFormItem = (type, items, onSelect, isInput = false, disabled = false) => {
    if (type === 'description' && orderDescription) {
      form.setFieldsValue({
        [allCreateOrderFields[type]?.name]: orderDescription,
      });
    }
    // eslint-disable-next-line max-len
    const tooltip = items?.filter(i => i.tooltip)?.map(i => `<p><b>${i.tooltip.businessIndicatorId}</b> - <i>${i.tooltip.name}</i></p><p>${i.tooltip.description}</p>`).join('</br>');

    if (isInput) {
      return (
        <Form.Item
          name={allCreateOrderFields[type]?.name}
          label={
            <>
              <p>{intl.formatMessage({
                id: `orderWizard.simplified.${allCreateOrderFields[type]?.label}`,
              })}</p>
              <InfoTooltip content={tooltip} />
            </>
          }
          rules={[{ required: true }]}
        >
          <MaxLengthInput
            name={allCreateOrderFields[type]?.name}
            max={40}
          />
        </Form.Item>
      );
    }
    if (fieldsToRender.includes(type)) {
      if (!items?.length) {
        return <Form.Item
          name={allCreateOrderFields[type]?.name}
          label=''
          style={{ display: 'none' }}
        >
          <Input type='text' />
        </Form.Item>;
      }
      return (
        <Form.Item
          name={allCreateOrderFields[type]?.name}
          label={
            <>
              <p>{intl.formatMessage({
                id: `orderWizard.simplified.${allCreateOrderFields[type]?.label}`,
              })}</p>
              <InfoTooltip content={tooltip} />
            </>
          }
          rules={[{ required: true }]}
        >
          <StyledSelect
            placeholder={
              intl.formatMessage({
                id: `orderWizard.simplified.${allCreateOrderFields[type]?.placeholder}`,
              })
            }
            options={items?.map(i => ({
              label: i.description,
              value: i.id,
              ...i,
            }))}
            disabled={
              isLoading ||
              isSubmitting ||
              !items?.length ||
              disabled
            }
            onSelect={type === 'businessIndicator' ?
              v => onSelect(v) :
              onSelect
            }
            showSearch={true}
            filterOption={(val, option) =>
              option.description?.toLowerCase().indexOf(val?.toLowerCase()) >= 0 ||
              option.label?.toLowerCase().indexOf(val?.toLowerCase()) >= 0 ||
              option.id?.toLowerCase().indexOf(val?.toLowerCase()) >= 0
            }
          />
        </Form.Item>
      );
    }
    return <Form.Item name={allCreateOrderFields[type]?.name} label='' style={{ display: 'none' }}>
      <Input type='text' />
    </Form.Item>;
  };

  const handlePaymentTypeSelect = (e, option) => {
    fieldsToRender.forEach((f) => {
      if (f !== 'serviceType' && f !== 'visitType' && f !== 'paidBy' && f !== 'paymentType') {
        form.setFieldsValue({
          [f]: null,
        });
      }
    });
    setAdditionalFields([]);

    if (!option.businessIndicators?.length) {
      checkBusinessSubTypes(option.value);
    }

    if (option.businessIndicators?.length) {
      setAdditionalFields(s => [
        ...s,
        {
          id: 'businessIndicator',
          dropdownValues: option.businessIndicators,
          onSelect: v =>
            handleAdditionalFieldSelect(
              () => checkBusinessSubTypes(v),
            ),
        },
      ]);
      setFieldsToRender(s => [...s, 'businessIndicator']);
      if (option.businessIndicators.length === 1) {
        form.setFieldsValue({
          businessIndicator: option.businessIndicators[0].id,
        });
        handleAdditionalFieldSelect(() => checkBusinessSubTypes(option.businessIndicators[0].id));
      }
    } else if (option.dropdown?.length) {
      const options = option.dropdown?.map(k => ({
        label: k.description,
        value: k.id,
        id: k.id,
      }));
      setAdditionalFields(s => [
        ...s,
        {
          id: e,
          dropdownValues: options,
          onSelect: () => handleAdditionalFieldSelect(),
        },
      ]);
      setFieldsToRender(s => [...s, e]);
      if (options.length === 1) {
        form.setFieldsValue({
          [allCreateOrderFields[e]?.name]: options[0].id,
        });
        handleAdditionalFieldSelect();
      }
    } else {
      setFieldsToRender(s => [...s, 'description']);
    }
  };

  const handlePaidBySelect = (e, option) => {
    fieldsToRender.forEach((f) => {
      if (f !== 'serviceType' && f !== 'visitType' && f !== 'paidBy') {
        form.setFieldsValue({
          [f]: null,
        });
      }
    });
    const filteredPaymentTypes = option.paymentTypes?.filter(i => conditionsAreValid(i.conditions));
    setPaymentTypes(filteredPaymentTypes);
    if (!visitTypes?.length) {
      setFieldsToRender(['serviceType', 'paidBy', 'paymentType']);
    } else {
      setFieldsToRender(['serviceType', 'visitType', 'paidBy', 'paymentType']);
    }
    if (filteredPaymentTypes.length === 1) {
      form.setFieldsValue({
        paymentType: filteredPaymentTypes[0].id,
      });
      handlePaymentTypeSelect(filteredPaymentTypes[0].id, filteredPaymentTypes[0]);
    }
  };

  const handleServiceTypeSelect = (e, option) => {
    fieldsToRender.forEach((f) => {
      if (f !== 'serviceType') {
        form.setFieldsValue({
          [f]: null,
        });
      }
    });
    setFieldsToRender(['serviceType']);
    if (option.visitTypes.length) {
      setFieldsToRender(['serviceType', 'visitType']);
    } else {
      setFieldsToRender(s => s?.filter(o => o !== 'visitType'));
      if (option.paidBys?.length) {
        setFieldsToRender(['serviceType', 'paidBy']);
      }
    }
    setVisitTypes(option.visitTypes);
    setPaidBys(option.paidBys);

    if (option.visitTypes.length === 1) {
      form.setFieldsValue({
        visitType: option.visitTypes[0].id,
      });
    }
    if (!option.visitTypes?.length && option.paidBys.length === 1) {
      form.setFieldsValue({
        paidBy: option.paidBys[0].id,
      });
      handlePaidBySelect(option.paidBys[0].id, option.paidBys[0]);
    }
  };

  const handleVisitTypeSelect = () => {
    setFieldsToRender(['serviceType', 'visitType', 'paidBy']);
    form.setFieldsValue({
      paidBy: null,
    });
    if (paidBys.length === 1) {
      form.setFieldsValue({
        paidBy: paidBys[0].id,
      });
      handlePaidBySelect(paidBys[0].id, paidBys[0]);
    }
  };

  const handleFormSubmit = () => {
    form.validateFields()
      .then((values) => {
        handleSubmit(values);
      })
      // eslint-disable-next-line no-console
      .catch(err => console.error(err));
  };

  return (
    <Form
      name='createOrderForm'
      layout='vertical'
      requiredMark={false}
      form={form}
    >
      <Row>
        <Col span={10}>
          {renderFormItem('serviceType', serviceTypes, handleServiceTypeSelect)}
        </Col>
        <Col span={1} />
        <Col span={10}>
          {renderFormItem('visitType', visitTypes, handleVisitTypeSelect)}
        </Col>
      </Row>
      <Row>
        <Col span={10}>
          {renderFormItem('paidBy', paidBys, handlePaidBySelect)}
        </Col>
        <Col span={1} />
        <Col span={10}>
          {renderFormItem('paymentType', paymentTypes, handlePaymentTypeSelect)}
        </Col>
      </Row>
      <Row>
        <Col span={10}>
          {additionalFields?.map(f =>
            renderFormItem(f.id, f.dropdownValues, f.onSelect, false, f.disabled),
          )}
        </Col>
      </Row>
      {fieldsToRender.includes('description') ?
        <Row>
          <Col span={10}>
            {renderFormItem('description', null, null, true)}
          </Col>
        </Row> :
        null
      }
      <Buttons>
        <Button
          loading={isSubmitting}
          onClick={cancel}
        >
          {intl.formatMessage({ id: 'common.CANCEL' })}
        </Button>
        <Button
          type='secondary'
          loading={isSubmitting}
          onClick={handleFormSubmit}
        >
          {intl.formatMessage({ id: 'orderWizard.CREATE_ORDER' })}
        </Button>
      </Buttons>
    </Form >
  );
};

SimplifiedCreateOrderForm.propTypes = {
  isLoading: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  intl: PropTypes.object,
  cancel: PropTypes.func,
  handleSubmit: PropTypes.func,
};

export default injectIntl(SimplifiedCreateOrderForm);
